import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { ThreeCanvas } from "@remotion/three";
import * as THREE from "three";

// --- PROCEDURAL 3D ZUI (CAT-FOX CHIBI) ---
const ZuiMesh: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const breathe = Math.sin(frame * 0.1) * 0.035;
  const bobY = Math.sin(frame * 0.12) * 0.04;
  const earTwitchLeft = Math.sin(frame * 0.18) * 0.08;
  const earTwitchRight = Math.cos(frame * 0.14) * 0.06;
  const tailWag = Math.sin(frame * 0.22) * 0.5;
  const wave = Math.sin(frame * 0.35) * 0.4;

  const blinkCycle = frame % 90;
  const isBlinking = blinkCycle > 82 && blinkCycle < 88;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // Rich tactile PBR Materials
  const furMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFBF5",
    roughness: 0.32,
    metalness: 0.04,
  });
  const bellyMaterial = new THREE.MeshStandardMaterial({
    color: "#FFE7D1",
    roughness: 0.4,
    metalness: 0.02,
  });
  const innerEarMaterial = new THREE.MeshStandardMaterial({
    color: "#FFA8BA",
    roughness: 0.35,
    metalness: 0.0,
  });
  const eyeGlossMaterial = new THREE.MeshStandardMaterial({
    color: "#080512",
    roughness: 0.02,
    metalness: 0.25,
  });
  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.08,
    metalness: 0.0,
  });
  const blushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF5E7E",
    emissive: new THREE.Color("#FF2255"),
    emissiveIntensity: 0.35,
    roughness: 0.4,
  });
  const noseMaterial = new THREE.MeshStandardMaterial({
    color: "#FF6B8B",
    roughness: 0.2,
    metalness: 0.08,
  });
  const collarMaterial = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#006688"),
    emissiveIntensity: 0.25,
    roughness: 0.15,
    metalness: 0.5,
  });
  const bellGoldMaterial = new THREE.MeshStandardMaterial({
    color: "#FFC800",
    roughness: 0.1,
    metalness: 0.95,
  });

  return (
    // Zui sitting on the left side of the podium floor
    <group position={[-0.88, -0.3 + bobY, 0]} rotation={[0, 0.32, 0]}>
      {/* Main Body */}
      <group scale={[1 + breathe, 1 - breathe * 0.5, 1 + breathe]}>
        <mesh material={furMaterial} castShadow receiveShadow position={[0, -0.22, 0]} scale={[1.08, 0.98, 1.05]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>
        <mesh material={bellyMaterial} castShadow receiveShadow position={[0, -0.25, 0.8]} scale={[0.82, 0.88, 0.32]}>
          <sphereGeometry args={[0.85, 24, 24]} />
        </mesh>
      </group>

      {/* Collar & Golden Bell */}
      <group position={[0, 0.42, 0]} rotation={[0.18, 0, 0]}>
        <mesh material={collarMaterial} castShadow receiveShadow>
          <torusGeometry args={[0.86, 0.1, 16, 36]} />
        </mesh>
        <mesh material={bellGoldMaterial} castShadow receiveShadow position={[0, -0.2, 0.92]}>
          <sphereGeometry args={[0.22, 24, 24]} />
        </mesh>
      </group>

      {/* Head */}
      <group position={[0, 1.18, 0]} rotation={[0, 0, 0.05]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[1.18, 1.02, 1.1]}>
          <sphereGeometry args={[1.0, 36, 36]} />
        </mesh>

        {/* Ears with cast shadows */}
        <group position={[-0.72, 0.95, -0.05]} rotation={[0.05, 0.1, 0.36 + earTwitchLeft]}>
          <mesh material={furMaterial} castShadow receiveShadow scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>
        <group position={[0.72, 0.95, -0.05]} rotation={[0.05, -0.1, -0.36 - earTwitchRight]}>
          <mesh material={furMaterial} castShadow receiveShadow scale={[0.5, 0.9, 0.38]}>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          <mesh material={innerEarMaterial} position={[0, -0.04, 0.12]} scale={[0.34, 0.68, 0.18]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>

        {/* Eyes looking towards Bolo */}
        <group position={[-0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} castShadow scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[0.08, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[-0.04, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>
        <group position={[0.42, 0.05, 1.02]} scale={[1, eyeScaleY, 1]}>
          <mesh material={eyeGlossMaterial} castShadow scale={[0.22, 0.26, 0.14]}>
            <sphereGeometry args={[1, 24, 24]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[0.08, 0.08, 0.12]} scale={[0.08, 0.08, 0.04]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
          <mesh material={eyeHighlightMaterial} position={[-0.04, -0.07, 0.12]} scale={[0.04, 0.04, 0.03]}>
            <sphereGeometry args={[1, 16, 16]} />
          </mesh>
        </group>

        {/* Blush Cheeks */}
        <mesh material={blushMaterial} position={[-0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={blushMaterial} position={[0.66, -0.15, 0.9]} scale={[0.22, 0.11, 0.05]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>

        {/* Nose & Smile */}
        <mesh material={noseMaterial} castShadow position={[0, -0.02, 1.12]} scale={[0.08, 0.055, 0.06]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <group position={[0, -0.14, 1.08]} rotation={[0, 0, Math.PI]}>
          <mesh material={noseMaterial} position={[-0.07, 0, 0]}>
            <torusGeometry args={[0.07, 0.02, 12, 18, Math.PI * 0.9]} />
          </mesh>
          <mesh material={noseMaterial} position={[0.07, 0, 0]}>
            <torusGeometry args={[0.07, 0.02, 12, 18, Math.PI * 0.9]} />
          </mesh>
        </group>
      </group>

      {/* Arms */}
      <group position={[-0.92, 0.08, 0.25]} rotation={[0.4, 0, 0.2]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.26, 0.42, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, -0.3, 0.06]} scale={[0.15, 0.15, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Right Arm (Waving towards Bolo) */}
      <group position={[0.95, 0.25, 0.25]} rotation={[-0.2, 0.2, -0.7 + wave]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.26, 0.45, 0.26]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.34, 0.04]} scale={[0.16, 0.16, 0.09]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Sitting Feet Planted on Floor */}
      <group position={[-0.52, -1.02, 0.58]} rotation={[-0.2, 0.2, 0]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
      <group position={[0.52, -1.02, 0.58]} rotation={[-0.2, -0.2, 0]}>
        <mesh material={furMaterial} castShadow receiveShadow scale={[0.34, 0.2, 0.45]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={innerEarMaterial} position={[0, 0.07, 0.13]} scale={[0.16, 0.05, 0.18]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Fluffy Tail */}
      <group position={[0, -0.45, -0.9]} rotation={[-0.35, tailWag, 0]}>
        <mesh material={furMaterial} castShadow scale={[0.22, 0.32, 0.7]} position={[0, 0.22, -0.22]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyMaterial} position={[0, 0.46, -0.6]} scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>
    </group>
  );
};

// --- PROCEDURAL 3D "BOLO" (ROUND BALL PET RESTING ON FLOOR - NOT FLOATING!) ---
const RoundBallBuddyMesh: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const breathe = Math.sin(frame * 0.12) * 0.025;
  const talkingMouth = Math.abs(Math.sin(frame * 0.4)) * 0.14;
  const handFlapLeft = Math.sin(frame * 0.3) * 0.22;
  const handFlapRight = Math.cos(frame * 0.32) * 0.25;
  const antennaSway = Math.sin(frame * 0.25) * 0.14;

  const blinkCycle = (frame + 35) % 85;
  const isBlinking = blinkCycle > 78 && blinkCycle < 84;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // Rich PBR Materials with soft clay shader & high specular highlights
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: "#BA78F8",
    roughness: 0.26,
    metalness: 0.12,
  });

  const bellyCreamMaterial = new THREE.MeshStandardMaterial({
    color: "#F5EDFF",
    roughness: 0.38,
    metalness: 0.02,
  });

  const cheekBlushMaterial = new THREE.MeshStandardMaterial({
    color: "#FF477E",
    emissive: new THREE.Color("#FF0055"),
    emissiveIntensity: 0.4,
    roughness: 0.3,
  });

  const eyeGlossMaterial = new THREE.MeshStandardMaterial({
    color: "#140824",
    roughness: 0.02,
    metalness: 0.3,
  });

  const eyeHighlightMaterial = new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.08,
    metalness: 0.0,
  });

  const mouthMaterial = new THREE.MeshStandardMaterial({
    color: "#D946EF",
    emissive: new THREE.Color("#9333EA"),
    emissiveIntensity: 0.2,
    roughness: 0.2,
  });

  const antennaStemMaterial = new THREE.MeshStandardMaterial({
    color: "#F472B6",
    roughness: 0.18,
    metalness: 0.4,
  });

  const glowingOrbMaterial = new THREE.MeshStandardMaterial({
    color: "#FBBF24",
    emissive: new THREE.Color("#F59E0B"),
    emissiveIntensity: 0.9,
    roughness: 0.1,
    metalness: 0.6,
  });

  const footMaterial = new THREE.MeshStandardMaterial({
    color: "#A855F7",
    roughness: 0.32,
    metalness: 0.1,
  });

  return (
    // BOLO SITTING DIRECTLY ON THE PODIUM FLOOR (NOT FLOATING!)
    <group position={[0.92, -0.45, 0.1]} rotation={[0.02, -0.35, 0]}>
      {/* --- CHUBBY SQUISHY ROUND BODY RESTING ON FLOOR --- */}
      <group scale={[1.06 + breathe, 0.94 - breathe * 0.8, 1.04 + breathe]}>
        {/* Main Spherical Ball Body */}
        <mesh material={bodyMaterial} castShadow receiveShadow position={[0, 0, 0]}>
          <sphereGeometry args={[0.92, 40, 40]} />
        </mesh>

        {/* Soft Circular Cream Belly Patch */}
        <mesh material={bellyCreamMaterial} castShadow receiveShadow position={[0, -0.12, 0.74]} scale={[0.72, 0.68, 0.3]}>
          <sphereGeometry args={[0.88, 32, 32]} />
        </mesh>
      </group>

      {/* --- CUTE FEET PLANTED FIRMLY ON THE FLOOR --- */}
      <group position={[-0.42, -0.72, 0.4]} rotation={[-0.1, 0.2, 0]}>
        <mesh material={footMaterial} castShadow receiveShadow scale={[0.28, 0.16, 0.36]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyCreamMaterial} position={[0, 0.05, 0.1]} scale={[0.14, 0.05, 0.15]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      <group position={[0.42, -0.72, 0.4]} rotation={[-0.1, -0.2, 0]}>
        <mesh material={footMaterial} castShadow receiveShadow scale={[0.28, 0.16, 0.36]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
        <mesh material={bellyCreamMaterial} position={[0, 0.05, 0.1]} scale={[0.14, 0.05, 0.15]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* --- CUTE ANTENNA WITH GLOWING STAR BULB ON TOP --- */}
      <group position={[0, 0.88, 0]} rotation={[0, 0, antennaSway]}>
        <mesh material={antennaStemMaterial} castShadow position={[0, 0.18, 0]}>
          <cylinderGeometry args={[0.04, 0.05, 0.36, 16]} />
        </mesh>
        <mesh material={glowingOrbMaterial} castShadow position={[0, 0.4, 0]}>
          <sphereGeometry args={[0.15, 24, 24]} />
        </mesh>
        <mesh material={antennaStemMaterial} castShadow position={[0, 0.4, 0]} rotation={[Math.PI / 3, 0, frame * 0.06]}>
          <torusGeometry args={[0.22, 0.02, 12, 24]} />
        </mesh>
      </group>

      {/* --- EXPRESSIVE FACE (LOOKING TOWARDS ZUI) --- */}
      <group position={[-0.3, 0.12, 0.84]} scale={[1, eyeScaleY, 1]}>
        <mesh material={eyeGlossMaterial} castShadow scale={[0.17, 0.21, 0.11]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[-0.05, 0.07, 0.1]} scale={[0.06, 0.06, 0.03]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[0.05, -0.05, 0.1]} scale={[0.03, 0.03, 0.02]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      <group position={[0.3, 0.12, 0.84]} scale={[1, eyeScaleY, 1]}>
        <mesh material={eyeGlossMaterial} castShadow scale={[0.17, 0.21, 0.11]}>
          <sphereGeometry args={[1, 24, 24]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[-0.05, 0.07, 0.1]} scale={[0.06, 0.06, 0.03]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
        <mesh material={eyeHighlightMaterial} position={[0.05, -0.05, 0.1]} scale={[0.03, 0.03, 0.02]}>
          <sphereGeometry args={[1, 16, 16]} />
        </mesh>
      </group>

      {/* Cheek Blushes */}
      <mesh material={cheekBlushMaterial} position={[-0.52, -0.06, 0.76]} scale={[0.16, 0.09, 0.05]}>
        <sphereGeometry args={[1, 16, 16]} />
      </mesh>
      <mesh material={cheekBlushMaterial} position={[0.52, -0.06, 0.76]} scale={[0.16, 0.09, 0.05]}>
        <sphereGeometry args={[1, 16, 16]} />
      </mesh>

      {/* Animated Talking Mouth */}
      <group position={[0, -0.08, 0.9]} scale={[1, 1 + talkingMouth * 3.5, 1]}>
        <mesh material={mouthMaterial} scale={[0.11, 0.075, 0.05]}>
          <sphereGeometry args={[1, 18, 18]} />
        </mesh>
      </group>

      {/* Floating Stubby Ball Hands */}
      <group position={[-0.86, -0.08, 0.38]} rotation={[0.2, 0, -0.4 + handFlapLeft]}>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
      <group position={[0.86, -0.08, 0.38]} rotation={[0.2, 0, 0.4 + handFlapRight]}>
        <mesh material={bellyCreamMaterial} castShadow receiveShadow scale={[0.2, 0.2, 0.2]}>
          <sphereGeometry args={[1, 20, 20]} />
        </mesh>
      </group>
    </group>
  );
};

// --- REMOTION COMPOSITION: ZUI & BOLO (RICH 3D LIGHTING & GROUNDED CAST SHADOWS) ---
export const ZuiAndBuddy: React.FC = () => {
  const { width, height, fps } = useVideoConfig();
  const frame = useCurrentFrame();

  const introPop = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 100, mass: 0.8 },
  });

  const cardSlide = spring({
    frame: frame - 10,
    fps,
    config: { damping: 16, stiffness: 120 },
  });

  // Camera subtle drift
  const camRotY = Math.sin(frame * 0.02) * 0.08;
  const camZ = interpolate(frame, [0, 90], [13.2, 12.8], {
    extrapolateRight: "clamp",
  });

  // Stage Materials - Specular Charcoal Stone with glowing accent insets
  const podiumFloorMaterial = new THREE.MeshStandardMaterial({
    color: "#222430", // Rich slate stone with clear shadow contrast
    roughness: 0.35,
    metalness: 0.25,
  });

  const podiumRimMaterial = new THREE.MeshStandardMaterial({
    color: "#0F1015",
    roughness: 0.2,
    metalness: 0.5,
  });

  const cyanRingMaterial = new THREE.MeshStandardMaterial({
    color: "#00E5FF",
    emissive: new THREE.Color("#00E5FF"),
    emissiveIntensity: 0.9,
    roughness: 0.2,
  });

  const purpleRingMaterial = new THREE.MeshStandardMaterial({
    color: "#C084FC",
    emissive: new THREE.Color("#C084FC"),
    emissiveIntensity: 0.85,
    roughness: 0.2,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#08080B",
        overflow: "hidden",
        fontFamily: "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* CINEMATIC BACKDROP SPOTLIGHT */}
      <div
        style={{
          position: "absolute",
          top: "12%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 960,
          height: 960,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 248, 235, 0.12) 0%, rgba(0, 229, 255, 0.08) 35%, rgba(192, 132, 252, 0.06) 60%, transparent 75%)",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />

      {/* --- 3D CANVAS WITH FULL SHADOW MAPS & RICH LIGHTING --- */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          zIndex: 10,
          transform: `scale(${introPop})`,
          transformOrigin: "center center",
        }}
      >
        <ThreeCanvas
          shadows
          width={width}
          height={height}
          camera={{
            // Camera tilted slightly down (y = 0.55) to showcase real cast shadows on the floor!
            position: [Math.sin(camRotY) * camZ, 0.55, Math.cos(camRotY) * camZ],
            fov: 38,
          }}
          onCreated={({ gl }) => {
            gl.shadowMap.enabled = true;
            gl.shadowMap.type = THREE.PCFSoftShadowMap;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.15;
          }}
        >
          {/* 1. Deep Mood Ambient (prevents flat washing out!) */}
          <ambientLight intensity={0.65} color="#2A2A38" />

          {/* 2. Primary Dramatic Key Light (Top-Left-Front) -> Creates clear, angled cast shadows! */}
          <directionalLight
            castShadow
            position={[-5.5, 9.5, 6.5]}
            intensity={3.8}
            color="#FFF6E8"
            shadow-mapSize={[2048, 2048]}
            shadow-bias={-0.0004}
            shadow-camera-near={0.5}
            shadow-camera-far={25}
            shadow-camera-left={-5.5}
            shadow-camera-right={5.5}
            shadow-camera-top={5.5}
            shadow-camera-bottom={-5.5}
          />

          {/* 3. Secondary Soft Fill Light (Front-Right-Low) */}
          <directionalLight position={[6, 4, 5]} intensity={1.5} color="#E0E7FF" />

          {/* 4. Crisp Cyan Rim Light (Back-Right) to outline Zui's silhouette */}
          <directionalLight position={[5, 7, -6]} intensity={4.5} color="#00E5FF" />

          {/* 5. Crisp Violet Rim Light (Back-Left) to outline Bolo's silhouette */}
          <directionalLight position={[-5, 7, -6]} intensity={3.8} color="#C084FC" />

          {/* 6. Subtle Ground Bounce Light */}
          <pointLight position={[0, -0.6, 2.5]} intensity={1.2} color="#FED7AA" distance={6} />

          {/* --- SHARED STAGE / PODIUM (RECEIVES REAL SHADOWS!) --- */}
          <group position={[0, -1.05, 0]}>
            {/* Top Stone Floor Slab with shadow reception */}
            <mesh material={podiumFloorMaterial} receiveShadow position={[0, 0, 0]}>
              <cylinderGeometry args={[3.6, 3.6, 0.12, 48]} />
            </mesh>

            {/* Dark Beveled Rim Below */}
            <mesh material={podiumRimMaterial} position={[0, -0.16, 0]}>
              <cylinderGeometry args={[3.7, 3.9, 0.22, 48]} />
            </mesh>

            {/* Inset Glowing Indicator Rings */}
            <mesh material={cyanRingMaterial} position={[-0.88, 0.065, 0]}>
              <ringGeometry args={[1.35, 1.45, 36]} />
            </mesh>
            <mesh material={purpleRingMaterial} position={[0.92, 0.065, 0]}>
              <ringGeometry args={[1.35, 1.45, 36]} />
            </mesh>
          </group>

          {/* CHARACTER 1: ZUI (SITTING & CASTING SHADOWS) */}
          <ZuiMesh frame={frame} fps={fps} />

          {/* CHARACTER 2: BOLO (RESTING DIRECTLY ON FLOOR & CASTING SHADOWS) */}
          <RoundBallBuddyMesh frame={frame} fps={fps} />
        </ThreeCanvas>
      </div>

      {/* --- 2D OVERLAYS & HUD (TIKTOK SAFE ZONE: Y 210px - 1460px) --- */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 50,
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 210,
          paddingBottom: 420,
          boxSizing: "border-box",
        }}
      >
        {/* TOP HEADER */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            opacity: introPop,
            transform: `translateY(${(1 - introPop) * -30}px)`,
          }}
        >
          {/* Top Pill Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "rgba(20, 20, 26, 0.88)",
              border: "1.5px solid rgba(255, 255, 255, 0.15)",
              borderRadius: 999,
              padding: "8px 24px",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#FBBF24",
                boxShadow: "0 0 10px #FBBF24",
              }}
            />
            <span
              style={{
                color: "#F4F4F5",
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              REAL 3D SHADOWS • DIRECTIONAL LIGHTING
            </span>
          </div>

          {/* Title */}
          <h1
            style={{
              margin: 0,
              fontSize: 74,
              fontWeight: 900,
              letterSpacing: "-0.03em",
              color: "#FFFFFF",
              textShadow: "0 8px 32px rgba(0,0,0,0.8)",
              textAlign: "center",
              lineHeight: 1.05,
            }}
          >
            <span style={{ color: "#00E5FF" }}>ZUI</span> &{" "}
            <span style={{ color: "#C084FC" }}>BOLO</span>
          </h1>

          <div
            style={{
              color: "#A1A1AA",
              fontSize: 20,
              fontWeight: 600,
              letterSpacing: "0.04em",
            }}
          >
            Side by Side on the Stage • Grounded & Dynamic
          </div>
        </div>

        {/* BOTTOM DIALOGUE / CHAT CARD */}
        <div
          style={{
            width: 880,
            background: "rgba(18, 18, 22, 0.92)",
            border: "2px solid rgba(255, 255, 255, 0.12)",
            borderRadius: 28,
            padding: "24px 32px",
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.75), 0 0 40px rgba(0, 229, 255, 0.1)",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            transform: `translateY(${(1 - cardSlide) * 40}px)`,
            opacity: cardSlide,
          }}
        >
          {/* Card Top Row: Character Tags */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <span
                style={{
                  background: "rgba(0, 229, 255, 0.15)",
                  color: "#00E5FF",
                  border: "1px solid rgba(0, 229, 255, 0.35)",
                  padding: "6px 14px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                }}
              >
                ZUI (CHIBI SITTING)
              </span>
              <span
                style={{
                  background: "rgba(192, 132, 252, 0.18)",
                  color: "#C084FC",
                  border: "1px solid rgba(192, 132, 252, 0.4)",
                  padding: "6px 14px",
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                }}
              >
                BOLO (GROUNDED BALL)
              </span>
            </div>
            <span
              style={{
                color: "#71717A",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.05em",
              }}
            >
              PCF SOFT SHADOW MAPS
            </span>
          </div>

          {/* Dialogue exchange between the two buddies */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Zui's line */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div
                style={{
                  background: "#00E5FF",
                  color: "#000",
                  fontWeight: 900,
                  fontSize: 13,
                  padding: "3px 8px",
                  borderRadius: 6,
                  marginTop: 2,
                }}
              >
                ZUI
              </div>
              <div style={{ color: "#E4E4E7", fontSize: 19, fontWeight: 600 }}>
                "Nah gitu dong Bolo, duduk bareng di lantai! Bayangan kita di panggung jadi keliatan jelas banget sekarang!"
              </div>
            </div>

            {/* Bolo's line */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div
                style={{
                  background: "#C084FC",
                  color: "#000",
                  fontWeight: 900,
                  fontSize: 13,
                  padding: "3px 8px",
                  borderRadius: 6,
                  marginTop: 2,
                }}
              >
                BOLO
              </div>
              <div style={{ color: "#FFFFFF", fontSize: 19, fontWeight: 700 }}>
                "Hehe iya, kakiku nempel di panggung nih! Cahaya studio-nya juga bikin tekstur kita makin timbul dan nyata!"
              </div>
            </div>
          </div>

          {/* Specs Mini Bar */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
              paddingTop: 8,
              borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <div>
              <div style={{ color: "#71717A", fontSize: 12, fontWeight: 700 }}>SHADOW ENGINE</div>
              <div style={{ color: "#22C55E", fontSize: 16, fontWeight: 800 }}>PCF SOFT 2048</div>
            </div>
            <div>
              <div style={{ color: "#71717A", fontSize: 12, fontWeight: 700 }}>LIGHTING MODEL</div>
              <div style={{ color: "#FBBF24", fontSize: 16, fontWeight: 800 }}>5-POINT STUDIO</div>
            </div>
            <div>
              <div style={{ color: "#71717A", fontSize: 12, fontWeight: 700 }}>PERFORMANCE</div>
              <div style={{ color: "#00E5FF", fontSize: 16, fontWeight: 800 }}>SUPER LIGHTWEIGHT</div>
            </div>
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
